import moment from 'moment-timezone';
import {
  ORDER_NEWSPAPER_ORDER_COMPLETED,
  OrderNewspaperOrderCompletedEvent,
  ORDER_NEWSPAPER_ORDER_CONFIRMED,
  OrderNewspaperOrderConfirmedEvent,
  ORDER_NEWSPAPER_ORDER_DELETED,
  OrderNewspaperOrderDeletedEvent
} from '../../types/events';
import {
  NotFoundError,
  BadRequestError,
  InternalServerError
} from '../../errors/ColumnErrors';
import { getModelFromSnapshot, SnapshotModel } from '..';
import { Collections } from '../../constants';
import {
  NewspaperOrder,
  NewspaperOrderPricing,
  NewspaperOrderStatus
} from '../../types/newspaperOrder';
import {
  ResponseOrColumnError,
  ResponseOrError,
  wrapError,
  wrapSuccess
} from '../../types/responses';
import { getProductDeadlineTimeForPaper } from '../../utils/deadlines';
import { OrderModel } from './orderModel';
import { safeAsync } from '../../safeWrappers';
import { ColumnService } from '../../services/directory';
import { getErrorReporter } from '../../utils/errors';
import { OrganizationModel } from './organizationModel';
import {
  AdRate,
  Customer,
  CustomerOrganization,
  EInvoice,
  ERef,
  ETransaction,
  ESnapshotExists,
  EUser,
  FirebaseTimestamp
} from '../../types';
import { safeGetModelFromRef } from '../getModel';
import { UserModel } from './userModel';
import { PublicationIssue } from '../../types/publicationIssue';
import { getDateForDateStringInTimezone } from '../../utils/dates';
import CustomerService from '../../services/customerService';
import { isAnonymousOrder } from '../../types/order';
import CustomerOrganizationService from '../../services/customerOrganizationService';
import { BillingTermType } from '../../enums';
import { safeFirestoreTimestampOrDateToDate } from '../../helpers';
import { safeUpdateModel } from '../safeHandlers';
import { SerializedModel } from '../types';
import { ModularSizeModel } from './modularSizeModel';
import { FilingTypeModel } from './filingTypeModel';
import { PackageModel } from './packageModel';
import { LayoutModel } from './layoutModel';
import { InvoiceTransaction } from '../../types/invoiceTransaction';
import { NewspaperOrderService } from '../../services/newspaperOrderService';
import { getRefundAmountBreakdownInCents } from '../../payments/refunds';
import { NewspaperOrderTransactionService } from '../../services/newspaperOrderTransactionService';
import { NewspaperOrderTransactionModel } from './newspaperOrderTransactionModel';
import { NewspaperOrderTransaction } from '../../types/newspaperOrderTransaction';
import { CustomerModel } from './customerModel';
import { CustomerOrganizationModel } from './customerOrganizationModel';
import { TransferStatus } from '../../types/transfers';
import { hasPaymentOrPartialRefund } from '../../utils/invoices';
import { OrderInvoice } from '../../types/invoices';
import { mapAsync } from '../../helpers/array/async';

export type NewspaperOrderEditableData = {
  canEdit: boolean;
  isBeforeDeadline: boolean;
  bannerMessage: string | undefined;
  newspaperId: string;
};

export type SerializedNewspaperOrderModel = SerializedModel<
  typeof Collections.newspaperOrders
>;

export class NewspaperOrderModel extends SnapshotModel<
  NewspaperOrder,
  typeof Collections.newspaperOrders
> {
  private newspaper: OrganizationModel | null = null;

  get type() {
    return Collections.newspaperOrders;
  }

  get transferHasOccurred() {
    return !!this.modelData.transfer;
  }

  get firstPublishingDate() {
    return this.modelData.publishingDates.sort()[0];
  }

  public getOrderRef() {
    return this.ref.parent.parent;
  }

  public async getOrder(): Promise<ResponseOrColumnError<OrderModel>> {
    const orderRef = this.getOrderRef();
    if (!orderRef) {
      return wrapError(new NotFoundError('Order not found'));
    }
    return safeGetModelFromRef(OrderModel, this.ctx, orderRef);
  }

  public async getNewspaper(): Promise<
    ResponseOrColumnError<OrganizationModel>
  > {
    if (this.newspaper) {
      return wrapSuccess(this.newspaper);
    }
    const { response: newspaper, error } = await safeGetModelFromRef(
      OrganizationModel,
      this.ctx,
      this.modelData.newspaper
    );
    if (error) {
      return wrapError(error);
    }
    this.newspaper = newspaper;
    return wrapSuccess(newspaper);
  }

  public async getModularSize(): Promise<
    ResponseOrColumnError<ModularSizeModel | null>
  > {
    const { modularSize } = this.modelData;
    if (!modularSize) {
      return wrapSuccess(null);
    }
    return safeGetModelFromRef(ModularSizeModel, this.ctx, modularSize);
  }

  private getSortedPublishingDates() {
    return [...this.modelData.publishingDates].sort();
  }

  public async getDeadline(): Promise<ResponseOrError<moment.Moment>> {
    try {
      if (this.modelData.publishingDates.length === 0) {
        return wrapError(new Error('Newspaper order has no publishing dates'));
      }
      // TODO: Pass this in
      const newspaperResp = await this.getNewspaper();
      if (newspaperResp.error) {
        return wrapError(newspaperResp.error);
      }
      const newspaper = newspaperResp.response;
      const firstPublishingDateStr = this.getSortedPublishingDates()[0];
      const { response: order, error: getOrderError } = await this.getOrder();
      if (getOrderError) {
        return wrapError(getOrderError);
      }
      const { product } = order.modelData;
      const { response: deadlineResponse, error: deadlineError } =
        await getProductDeadlineTimeForPaper(
          newspaper,
          product,
          this.modelData.publishingMedium,
          firstPublishingDateStr
        );
      if (deadlineError) {
        return wrapError(deadlineError);
      }
      if (deadlineResponse === null) {
        const err = new Error('Deadline result response is null');
        getErrorReporter().logAndCaptureCriticalError(
          ColumnService.ORDER_PLACEMENT,
          err,
          'Failed to get deadline for newspaper order',
          {
            newspaperOrderId: this.id,
            orderId: order.id,
            product
          }
        );
        return wrapError(err);
      }
      return wrapSuccess(deadlineResponse.deadlineMoment);
    } catch (err) {
      return wrapError(err as Error);
    }
  }

  public async getFilingType(): Promise<
    ResponseOrColumnError<FilingTypeModel>
  > {
    return safeGetModelFromRef(
      FilingTypeModel,
      this.ctx,
      this.modelData.filingType
    );
  }

  public async getPackage(): Promise<
    ResponseOrColumnError<PackageModel | null>
  > {
    if (!this.modelData.package) {
      return wrapSuccess(null);
    }
    return safeGetModelFromRef(PackageModel, this.ctx, this.modelData.package);
  }

  public async getRate(): Promise<ResponseOrError<ESnapshotExists<AdRate>>> {
    const layoutModel = new LayoutModel(this.modelData.layout);

    const [customerOrgError, customerOrg] =
      await this.getCustomerOrganization();

    if (customerOrgError) {
      return wrapError(customerOrgError);
    }

    if (customerOrg) {
      const customerOrgModel = getModelFromSnapshot(
        CustomerOrganizationModel,
        this.ctx,
        customerOrg
      );

      const [customerRateError, customerRate] =
        await customerOrgModel.getDefaultFilingTypeRate(
          this.modelData.filingType.id,
          { isDisplayLayout: layoutModel.isDisplayLayout }
        );

      if (customerRateError) {
        return wrapError(customerRateError);
      }

      if (customerRate) {
        return wrapSuccess(customerRate);
      }
    }

    const [customerError, customer] = await this.getCustomer();

    if (customerError) {
      return wrapError(customerError);
    }

    if (customer) {
      const customerModel = getModelFromSnapshot(
        CustomerModel,
        this.ctx,
        customer
      );
      const [customerRateError, customerRate] =
        await customerModel.getDefaultFilingTypeRate(
          this.modelData.filingType.id,
          { isDisplayLayout: layoutModel.isDisplayLayout }
        );
      if (customerRateError) {
        return wrapError(customerRateError);
      }
      if (customerRate) {
        return wrapSuccess(customerRate);
      }
    }

    const [packageError, packageModel] = await this.getPackage();
    if (packageError) {
      return wrapError(packageError);
    }

    if (packageModel) {
      const [publisherOrganizationError, publisherOrganization] =
        await this.getNewspaper();
      if (publisherOrganizationError) {
        return wrapError(publisherOrganizationError);
      }

      const [packageRateError, packageRate] = await packageModel.getPackageRate(
        {
          forPublisherOrganization: publisherOrganization,
          forPublishingMedium: this.modelData.publishingMedium
        }
      );

      if (packageRateError) {
        return wrapError(packageRateError);
      }

      if (packageRate) {
        return wrapSuccess(packageRate);
      }
    }

    const { response: filingType, error: filingTypeError } =
      await this.getFilingType();
    if (filingTypeError) {
      return wrapError(filingTypeError);
    }

    return await filingType.getRate({
      isDisplayLayout: layoutModel.isDisplayLayout
    });
  }

  public async setTransferToInProgress(): Promise<ResponseOrError<void>> {
    const orderRef = this.getOrderRef();
    const [error] = await safeAsync(() =>
      this.ctx.runTransaction(async (tx: ETransaction) => {
        const newspaperOrder = await tx.get(this.ref);
        const newspaperOrderData = newspaperOrder.data();
        if (!newspaperOrderData) {
          throw new NotFoundError('Newspaper order not found');
        }

        if (newspaperOrderData.transferStatus !== TransferStatus.PENDING) {
          throw new BadRequestError(
            `Newspaper order transfer status is not PENDING: ${newspaperOrderData.transferStatus}`
          );
        }
        tx.update(this.ref, { transferStatus: TransferStatus.IN_PROGRESS });
      })
    )();
    if (error) {
      getErrorReporter().logAndCaptureError(
        ColumnService.FINANCIAL_RECONCILIATION,
        error,
        'Unexpected error during transaction setting transfer status to IN_PROGRESS',
        {
          newspaperOrderId: this.id,
          orderId: orderRef?.id || 'unknown'
        }
      );
      return wrapError(error);
    }
    const [refreshError] = await safeAsync(() => this.refreshData())();
    if (refreshError) {
      getErrorReporter().logAndCaptureError(
        ColumnService.FINANCIAL_RECONCILIATION,
        refreshError,
        'Unexpected error during refresh after setting transfer status to IN_PROGRESS',
        { newspaperOrderId: this.id, orderId: orderRef?.id || 'unknown' }
      );
      return wrapError(refreshError);
    }
    return wrapSuccess(undefined);
  }

  /**
   * Updates the status of the newspaper order and handles initiating required side-effects
   * Additionally initializes transferStatus to 'pending' if not yet set and status moves to COMPLETE.
   */
  public async updateStatus(
    status: NewspaperOrderStatus,
    confirmedBy?: ERef<EUser>
  ): Promise<ResponseOrError<void>> {
    const updates: Partial<NewspaperOrder> = { status };
    if (status === this.modelData.status) {
      getErrorReporter().logInfo(
        'Newspaper order status update called with same status',
        {
          newspaperOrderId: this.id,
          status
        }
      );
      return wrapSuccess(undefined);
    }

    if (
      status !== NewspaperOrderStatus.COMPLETE &&
      this.modelData.transferStatus === TransferStatus.PENDING
    ) {
      updates.transferStatus = this.ctx.fieldValue().delete() as any;
      getErrorReporter().logInfo(
        'Clearing transferStatus from PENDING due to status changing to non-COMPLETE status',
        {
          newspaperOrderId: this.id,
          previousStatus: this.modelData.status,
          nextStatus: status
        }
      );
    }
    if (
      !this.modelData.transferStatus &&
      !this.transferHasOccurred &&
      status === NewspaperOrderStatus.COMPLETE
    ) {
      updates.transferStatus = TransferStatus.PENDING;
      getErrorReporter().logInfo(
        'Initializing transferStatus to PENDING on newspaper order status update',
        {
          newspaperOrderId: this.id,
          status
        }
      );
    }
    const [orderError, order] = await this.getOrder();
    if (orderError) {
      return wrapError(orderError);
    }
    switch (status) {
      case NewspaperOrderStatus.CONFIRMED:
        return this.handleConfirmedUpdate(order, updates, confirmedBy);
      case NewspaperOrderStatus.COMPLETE:
        return this.handleCompleteUpdate(order, updates);
      case NewspaperOrderStatus.DELETED:
        return this.handleDeletedUpdate(order, updates);
      default:
        return safeAsync(() => this.update(updates))();
    }
  }

  private async handleConfirmedUpdate(
    order: OrderModel,
    updates: Partial<NewspaperOrder>,
    confirmedBy?: ERef<EUser>
  ): Promise<ResponseOrError<void>> {
    if (!confirmedBy) {
      return wrapError(new Error('Newspaper order confirmed anonymously'));
    }
    const confirmedAt = this.ctx
      .fieldValue()
      .serverTimestamp() as FirebaseTimestamp;
    const { error: createEventError } =
      // Has side effects
      await order.createEvent<OrderNewspaperOrderConfirmedEvent>({
        type: ORDER_NEWSPAPER_ORDER_CONFIRMED,
        data: {
          newspaperOrder: this.ref,
          confirmedBy
        },
        handleSideEffects: true
      });
    if (createEventError) {
      return wrapError(createEventError);
    }
    return await safeUpdateModel(this, {
      ...updates,
      confirmedAt
    });
  }

  private async handleCompleteUpdate(
    order: OrderModel,
    updates: Partial<NewspaperOrder>
  ): Promise<ResponseOrError<void>> {
    const [, invoice] = await order.getInvoice();
    const [canMarkAsCompleteError, canMarkAsComplete] =
      await this.areCompletionConditionsMet(invoice);
    if (canMarkAsCompleteError) {
      return wrapError(canMarkAsCompleteError);
    }
    /**
     * This is considered an error rather than just an early return because in
     * `markOrdersAsComplete`, we query orders from Elastic that match these criteria,
     * so if this returns false, then something may have gone wrong in that query or
     * in the sync to Elastic.
     */
    if (!canMarkAsComplete) {
      const err = new Error(
        'Newspaper order does not meet conditions to be marked as complete'
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        err,
        'Error marking newspaper order as complete',
        {
          newspaperOrderId: this.id,
          orderId: this.getOrderRef()?.id || 'unknown'
        }
      );
      return wrapError(err);
    }
    const [createEventError] =
      // Has side effects
      await order.createEvent<OrderNewspaperOrderCompletedEvent>({
        type: ORDER_NEWSPAPER_ORDER_COMPLETED,
        data: {
          newspaperOrder: this.ref
        },
        handleSideEffects: true
      });
    if (createEventError) {
      return wrapError(createEventError);
    }
    return await safeUpdateModel(this, updates);
  }

  private async handleDeletedUpdate(
    order: OrderModel,
    updates: Partial<NewspaperOrder>
  ): Promise<ResponseOrError<void>> {
    const [createEventError] =
      // Has side effects
      await order.createEvent<OrderNewspaperOrderDeletedEvent>({
        type: ORDER_NEWSPAPER_ORDER_DELETED,
        data: {
          newspaperOrder: this.ref
        },
        handleSideEffects: true
      });
    if (createEventError) {
      return wrapError(createEventError);
    }
    return await safeUpdateModel(this, updates);
  }

  public async isAtLeastNHoursBeforeDeadline(
    hoursBeforeDeadline: number
  ): Promise<ResponseOrError<boolean>> {
    const { response: deadline, error: deadlineError } =
      await this.getDeadline();
    if (deadlineError) {
      return wrapError(deadlineError);
    }

    return wrapSuccess(
      moment().add(hoursBeforeDeadline, 'hours').isBefore(deadline)
    );
  }

  private async getEditableDataForPublisher(
    user: UserModel,
    isBeforeDeadline: boolean
  ): Promise<ResponseOrError<NewspaperOrderEditableData>> {
    const newspaperId = this.modelData.newspaper.id;
    const userIsPartOfNewspaper = user.modelData.allowedOrganizations?.some(
      org => org.id === newspaperId
    );
    const newspaperOrderIsAwaitingReview =
      this.modelData.status === NewspaperOrderStatus.AWAITING_REVIEW;
    const userCanEditOwnNewspaper =
      userIsPartOfNewspaper && !this.transferHasOccurred;
    const userCanEditForAnotherNewspaper =
      isBeforeDeadline && newspaperOrderIsAwaitingReview;

    const canEdit = userCanEditOwnNewspaper || userCanEditForAnotherNewspaper;
    const bannerMessage =
      isBeforeDeadline && canEdit
        ? undefined
        : userIsPartOfNewspaper && canEdit
        ? 'This order has passed ad deadline. If you edit this order, ensure that the updated proof is sent to pagination to prevent incorrect content from publishing.'
        : userIsPartOfNewspaper && !canEdit
        ? 'A transfer has occurred for this order. No edits can be processed.'
        : !isBeforeDeadline
        ? 'The ad deadline for this order has passed. No edits or cancellations can be processed.'
        : "This publication has updated this order's confirmation status. You cannot edit this portion of the order.";
    return wrapSuccess({
      canEdit,
      bannerMessage,
      isBeforeDeadline,
      newspaperId
    });
  }

  private async getEditableDataForCustomer(
    isBeforeDeadline: boolean
  ): Promise<ResponseOrError<NewspaperOrderEditableData>> {
    const {
      response: isAtLeastOneHourBeforeDeadline,
      error: checkDeadlineError
    } = await this.isAtLeastNHoursBeforeDeadline(1);
    if (checkDeadlineError) {
      return wrapError(checkDeadlineError);
    }

    const canEdit = isAtLeastOneHourBeforeDeadline;
    const bannerMessage = canEdit
      ? 'Edits can be made to your order until one hour before the ad deadline. You can cancel your order until the ad deadline.'
      : isBeforeDeadline
      ? 'Edits cannot be made when it is less than one hour before the ad deadline. You can still cancel your order until the ad deadline.'
      : 'The ad deadline for this order has passed. No edits or cancellations can be processed.';
    return wrapSuccess({
      canEdit,
      bannerMessage,
      isBeforeDeadline,
      newspaperId: this.modelData.newspaper.id
    });
  }

  public async getEditableDataForUser(
    user: UserModel | null
  ): Promise<ResponseOrError<NewspaperOrderEditableData>> {
    const { response: isBeforeDeadline, error: isBeforeDeadlineError } =
      await this.isAtLeastNHoursBeforeDeadline(0);
    if (isBeforeDeadlineError) {
      return wrapError(isBeforeDeadlineError);
    }

    // If the newspaper order is cancelled, it can't be edited
    if (this.modelData.status === NewspaperOrderStatus.CANCELLED) {
      return wrapSuccess({
        canEdit: false,
        bannerMessage: undefined,
        isBeforeDeadline,
        newspaperId: this.modelData.newspaper.id
      });
    }

    if (user && user.isPublisher) {
      return this.getEditableDataForPublisher(user, isBeforeDeadline);
    }

    return this.getEditableDataForCustomer(isBeforeDeadline);
  }

  public async getPublicationIssues(): Promise<
    ResponseOrError<ESnapshotExists<PublicationIssue>[]>
  > {
    const publishingDates = this.modelData.publishingDates || [];
    if (!publishingDates.length) {
      return wrapSuccess([]);
    }

    // Firestore `in` queries support a max of 30 values.
    const maxInClause = 30;
    const dateChunks: string[][] = [];
    for (let i = 0; i < publishingDates.length; i += maxInClause) {
      dateChunks.push(publishingDates.slice(i, i + maxInClause));
    }

    const publicationIssuesRef = this.ctx.publicationIssuesRef();
    const [chunkError, chunkDocs] = await mapAsync(
      dateChunks,
      async dateChunk => {
        const query = publicationIssuesRef
          .where('publisher', '==', this.modelData.newspaper)
          .where('publicationDate', 'in', dateChunk);

        const [queryError, publicationIssues] = await safeAsync(() =>
          query.get()
        )();
        if (queryError) {
          return wrapError(queryError);
        }

        return wrapSuccess(publicationIssues.docs);
      }
    );

    if (chunkError) {
      return wrapError(chunkError);
    }

    const uniqueIssuesById = new Map(
      chunkDocs.flat().map(issueSnap => [issueSnap.id, issueSnap])
    );
    return wrapSuccess(Array.from(uniqueIssuesById.values()));
  }

  private get isConfirmed() {
    return this.modelData.status === NewspaperOrderStatus.CONFIRMED;
  }

  public get isComplete() {
    return this.modelData.status === NewspaperOrderStatus.COMPLETE;
  }

  private async hasLastPublishingDateElapsed(): Promise<
    ResponseOrError<boolean>
  > {
    const lastPublishingDateStr = this.getSortedPublishingDates().pop();
    if (!lastPublishingDateStr) {
      const err = new Error(
        'Unable to determine last publishing date for newspaper order'
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        err,
        'Error determining last publishing date for newspaper order',
        {
          newspaperOrderId: this.id,
          orderId: this.getOrderRef()?.id || 'unknown'
        }
      );
      return wrapError(err);
    }

    const { response: newspaper, error: newspaperError } =
      await this.getNewspaper();
    if (newspaperError) {
      return wrapError(newspaperError);
    }

    const timezone = newspaper.modelData.iana_timezone;
    const lastPublishingDateMoment = moment(
      getDateForDateStringInTimezone({
        dayString: lastPublishingDateStr,
        timezone
      })
    );
    const today = moment().tz(timezone);
    return wrapSuccess(today.isSameOrAfter(lastPublishingDateMoment, 'day'));
  }

  private async areCompletionConditionsMet(
    invoice: ESnapshotExists<OrderInvoice> | null
  ): Promise<ResponseOrError<boolean>> {
    const hasPaidInvoice = !!invoice && hasPaymentOrPartialRefund(invoice);
    if (!hasPaidInvoice) {
      const err = new BadRequestError(
        'Cannot mark newspaper order as complete without a paid invoice'
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.ORDER_MANAGEMENT,
        err,
        'Error marking newspaper order as complete',
        {
          newspaperOrderId: this.id,
          orderId: this.getOrderRef()?.id || 'unknown',
          invoiceId: invoice ? invoice.id : 'none',
          invoiceStatus: invoice ? `${invoice.data().status}` : 'none'
        }
      );
      return wrapError(err);
    }
    const [lastPublishingDateError, lastPublishingDateHasElapsed] =
      await this.hasLastPublishingDateElapsed();
    if (lastPublishingDateError) {
      return wrapError(lastPublishingDateError);
    }
    if (!lastPublishingDateHasElapsed) {
      getErrorReporter().logInfo(
        'Last publishing date has not elapsed for newspaper order.  Completion condition not met.',
        {
          newspaperOrderId: this.id
        }
      );
      return wrapSuccess(false);
    }
    if (!this.isConfirmed) {
      getErrorReporter().logInfo(
        'Newspaper order is not confirmed.  Completion condition not met.',
        {
          newspaperOrderId: this.id
        }
      );
      return wrapSuccess(false);
    }
    return wrapSuccess(true);
  }

  public async getCustomer(): Promise<
    ResponseOrError<ESnapshotExists<Customer> | null>
  > {
    const [orderError, order] = await this.getOrder();
    if (orderError) {
      return wrapError(orderError);
    }
    if (isAnonymousOrder(order.modelData)) {
      return wrapSuccess(null);
    }

    const { advertiser } = order.modelData;
    const { newspaper } = this.modelData;
    return await new CustomerService(this.ctx).maybeGetCustomerFromUserAndOrg(
      advertiser,
      newspaper
    );
  }

  public async getCustomerOrganization(): Promise<
    ResponseOrError<ESnapshotExists<CustomerOrganization> | null>
  > {
    const [orderError, order] = await this.getOrder();
    if (orderError) {
      return wrapError(orderError);
    }
    if (isAnonymousOrder(order.modelData)) {
      return wrapSuccess(null);
    }

    const { advertiserOrganization } = order.modelData;
    if (!advertiserOrganization) {
      return wrapSuccess(null);
    }
    const { newspaper } = this.modelData;
    return await new CustomerOrganizationService(
      this.ctx
    ).maybeGetCustomerOrgFromAdvertiserOrgAndNewspaper(
      advertiserOrganization,
      newspaper
    );
  }

  public async getRequiresUpfrontPayment(): Promise<ResponseOrError<boolean>> {
    const newspaperOrderService = new NewspaperOrderService(this.ctx);
    const [orderError, order] = await this.getOrder();
    if (orderError) {
      return wrapError(orderError);
    }
    const [billingTermError, billingTerm] =
      await newspaperOrderService.getOperativeBillingTerm(
        order.modelData,
        this.modelData
      );
    if (billingTermError) {
      return wrapError(billingTermError);
    }
    if (!billingTerm || billingTerm === BillingTermType.default.value) {
      return wrapError(
        new Error(
          'Got unexpected default billing term when determining upfront payment requirement for newspaper order'
        )
      );
    }

    return wrapSuccess(billingTerm === BillingTermType.upfront_payment.value);
  }

  public async calculateInvoiceDueDateByBillingTerm(
    invoiceCreateTime: EInvoice['created']
  ): Promise<ResponseOrError<number>> {
    const newspaperOrderService = new NewspaperOrderService(this.ctx);
    const firstPublishingDate = this.getSortedPublishingDates()[0];
    if (!firstPublishingDate) {
      return wrapError(
        new Error(
          'Cannot determine invoice due date for newspaper order with no publishing dates'
        )
      );
    }

    const [orderError, order] = await this.getOrder();
    if (orderError) {
      return wrapError(orderError);
    }
    const [billingTermError, billingTerm] =
      await newspaperOrderService.getOperativeBillingTerm(
        order.modelData,
        this.modelData
      );
    if (billingTermError) {
      return wrapError(billingTermError);
    }

    const [deadlineError, deadline] = await this.getDeadline();
    if (deadlineError) {
      return wrapError(deadlineError);
    }

    const [newspaperError, newspaper] = await this.getNewspaper();
    if (newspaperError) {
      return wrapError(newspaperError);
    }
    const { iana_timezone: timezone } = newspaper.modelData;

    const [invoiceCreateTimeDateError, invoiceCreateTimeDate] =
      safeFirestoreTimestampOrDateToDate(invoiceCreateTime);
    if (invoiceCreateTimeDateError) {
      return wrapError(invoiceCreateTimeDateError);
    }

    /**
     * This is the default due date we were using before we started using the billing term
     * to determine the due date. We use this if the order is invoiced outside Column
     * or if we for some reason can't determine the billing term (which is an error we should log),
     * but we also use it for customers with the net 30 billing term since that is currently what
     * we use for notice invoices and this helps to standardize due dates across the two schemas.
     * see https://columnpbc.atlassian.net/browse/APP-3546?focusedCommentId=82243
     */
    const defaultDueDate = moment(invoiceCreateTimeDate).add(1, 'month').unix();
    switch (billingTerm) {
      case BillingTermType.thirty_days_end_of_month.value:
        return wrapSuccess(
          moment(invoiceCreateTimeDate)
            .tz(timezone)
            .add(1, 'month')
            .endOf('month')
            .unix()
        );
      case BillingTermType.upfront_payment.value:
        return wrapSuccess(deadline.unix());
      case BillingTermType.net_thirty.value:
      case BillingTermType.outside_column.value:
        return wrapSuccess(defaultDueDate);
      case BillingTermType.default.value:
      default:
        getErrorReporter().logAndCaptureCriticalError(
          ColumnService.PAYMENTS,
          new Error('Unable to determine order invoice due date'),
          'Unable to determine order invoice due date',
          {
            newspaperOrderId: this.id,
            orderId: this.getOrderRef()?.id || 'unknown',
            billingTerm: `${billingTerm}`
          }
        );
        return wrapSuccess(defaultDueDate);
    }
  }

  /**
   * Light helper with error logging to assert a newspaperOrder has pricing and return the property
   */
  private mustGetPricing(): ResponseOrColumnError<NewspaperOrderPricing> {
    if (!this.modelData.pricing) {
      const err = new InternalServerError(
        `Missing pricing for newspaper order ${this.id}`
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        err,
        'Missing pricing for newspaper order',
        {
          newspaperOrderId: this.id,
          orderId: this.getOrderRef()?.id || 'unknown'
        }
      );
      return wrapError(err);
    }
    return wrapSuccess(this.modelData.pricing);
  }

  /**
   * Stage a refund: creates a pending NewspaperOrderTransaction. No pricing mutation.
   * Returns the created transaction ref for later completion/cancellation.
   */
  public async stageRefund(
    refundInCents: number,
    reason: string | undefined,
    options: { transaction?: ETransaction } = {}
  ): Promise<ResponseOrColumnError<ERef<NewspaperOrderTransaction>>> {
    if (refundInCents <= 0) {
      return wrapError(new BadRequestError('Refund amount must be > 0'));
    }
    const [pricingError, pricing] = this.mustGetPricing();
    if (pricingError) {
      return wrapError(pricingError);
    }
    const logData = {
      newspaperOrderId: this.id,
      orderId: this.getOrderRef()?.id || 'unknown',
      refundInCents: refundInCents.toString()
    };
    const pendingRefundInCents = pricing.pendingRefundInCents ?? 0;
    const totalRefundedInCents = pricing.totalRefundedInCents ?? 0;
    const anticipatedTotalRefund =
      totalRefundedInCents + pendingRefundInCents + refundInCents;
    if (anticipatedTotalRefund > pricing.totalInCents) {
      const err = new InternalServerError(
        'Pending refund would exceed total amount of newspaper order'
      );
      getErrorReporter().logAndCaptureError(
        ColumnService.PAYMENTS,
        err,
        'Failed to stage refund due to amount validation',
        logData
      );
      return wrapError(err);
    }

    const {
      publisherRefundPortionInCents,
      convenienceFeeRefundPortionInCents,
      taxRefundPortionInCents,
      applicationFeeRefundPortionInCents
    } = getRefundAmountBreakdownInCents({
      refundAmountInCents: refundInCents,
      pricing
    });

    const service = new NewspaperOrderTransactionService(this.ctx, this.ref);
    const [createPendingError, newspaperOrderTransactionRef] =
      await service.createPendingRefundTransaction({
        publisherRefundPortionInCents,
        convenienceFeeRefundPortionInCents,
        taxRefundPortionInCents,
        applicationFeeRefundPortionInCents,
        reason,
        logData,
        options: { transaction: options.transaction }
      });
    if (createPendingError) {
      return wrapError(createPendingError);
    }
    const [updateErr] = await safeUpdateModel(
      this,
      {
        pricing: {
          ...pricing,
          pendingRefundInCents: pendingRefundInCents + refundInCents
        }
      },
      { transaction: options.transaction }
    );
    if (updateErr) {
      return wrapError(updateErr);
    }
    getErrorReporter().logInfo('Staged refund successfully', logData);
    return wrapSuccess(newspaperOrderTransactionRef);
  }

  /**
   * Complete a previously staged refund: updates transaction status and applies pricing changes.
   */
  public async completeRefund(
    newspaperOrderTransaction: NewspaperOrderTransactionModel,
    invoiceTransactionRef: ERef<InvoiceTransaction>,
    options: { transaction?: ETransaction } = {}
  ): Promise<ResponseOrColumnError<void>> {
    const [pricingError, pricing] = this.mustGetPricing();
    if (pricingError) {
      return wrapError(pricingError);
    }
    const logData = {
      newspaperOrderId: this.id,
      newspaperOrderTransactionId: newspaperOrderTransaction.id,
      orderId: this.getOrderRef()?.id || 'unknown',
      invoiceTransactionId: invoiceTransactionRef.id
    };
    const [err] = await newspaperOrderTransaction.markPendingRefundComplete(
      invoiceTransactionRef,
      logData,
      { transaction: options.transaction }
    );
    if (err) {
      return wrapError(err);
    }
    const { modelData: attributionData } = newspaperOrderTransaction;

    const updatedPricing = {
      ...pricing,
      totalRefundedInCents:
        (pricing.totalRefundedInCents || 0) +
        attributionData.newspaperOrderAmountInCents,
      publisherAmountRefundedInCents:
        (pricing.publisherAmountRefundedInCents || 0) +
        attributionData.publisherPortionInCents,
      convenienceFeeRefundedInCents:
        (pricing.convenienceFeeRefundedInCents || 0) +
        attributionData.convenienceFeePortionInCents,
      taxesRefundedInCents:
        (pricing.taxesRefundedInCents || 0) + attributionData.taxPortionInCents,
      applicationFeeRefundedInCents:
        (pricing.applicationFeeRefundedInCents || 0) +
        (attributionData.applicationFeePortionInCents || 0),
      pendingRefundInCents: Math.max(
        0,
        (pricing.pendingRefundInCents || 0) -
          attributionData.newspaperOrderAmountInCents
      )
    };
    const netPublisherAmountInCents =
      pricing.publisherAmountInCents -
      updatedPricing.publisherAmountRefundedInCents;
    if (netPublisherAmountInCents < 0) {
      const err = new InternalServerError(
        'Net publisher remaining became negative during refund completion'
      );
      getErrorReporter().logAndCaptureCriticalError(
        ColumnService.PAYMENTS,
        err,
        'Error attributing refund after payment gateway processing!  Very bad, and requires manual investigation!',
        logData
      );
      return wrapError(err);
    }
    updatedPricing.netPublisherAmountInCents = netPublisherAmountInCents;
    const [updateErr] = await safeUpdateModel(
      this,
      {
        pricing: updatedPricing
      },
      { transaction: options.transaction }
    );
    if (updateErr) {
      return wrapError(updateErr);
    }
    getErrorReporter().logInfo('Refund completed successfully', logData);
    return wrapSuccess(undefined);
  }

  /**
   * Cancel a staged refund
   */
  public async cancelRefund(
    newspaperOrderTransaction: NewspaperOrderTransactionModel,
    options: { transaction?: ETransaction } = {}
  ): Promise<ResponseOrColumnError<void>> {
    const logData = {
      newspaperOrderId: this.id,
      newspaperOrderTransactionId: newspaperOrderTransaction.id,
      orderId: this.getOrderRef()?.id || 'unknown'
    };
    const [pricingError, pricing] = this.mustGetPricing();
    if (pricingError) {
      return wrapError(pricingError);
    }
    const [cancelErr] = await newspaperOrderTransaction.cancelPendingRefund(
      logData,
      { transaction: options.transaction }
    );
    if (cancelErr) {
      return wrapError(cancelErr);
    }

    const amount =
      newspaperOrderTransaction.modelData.newspaperOrderAmountInCents || 0;
    const pendingRefundInCents = Math.max(
      0,
      (pricing.pendingRefundInCents || 0) - amount
    );
    const [updateErr] = await safeUpdateModel(
      this,
      {
        pricing: {
          ...pricing,
          pendingRefundInCents
        }
      },
      { transaction: options.transaction }
    );
    if (updateErr) {
      return wrapError(updateErr);
    }
    getErrorReporter().logInfo('Refund cancelled successfully', logData);
    return wrapSuccess(undefined);
  }
}
